import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Product {
  id: number;
  name: string;
  title?: string;
  price: string;
  image: string | null;
  stock: number;
  status: string;
  category_name?: string;
  is_featured: number;
  is_special?: number;
  created_at: string;
  description?: string;
}

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ── نمایش Toast (دیالوگ باکس) ──────────────────────────
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── دریافت لیست محصولات ──────────────────────────────
  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://fellfell.ir/api/products.php', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 محصولات دریافت شد:', data);
      
      if (data.success && Array.isArray(data.data)) {
        setProducts(data.data);
      } else {
        setError('خطا در دریافت محصولات: ساختار داده نامعتبر');
      }
    } catch (err: any) {
      console.error('❌ خطا:', err);
      setError('خطا در ارتباط با سرور: ' + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // ── حذف محصول ──────────────────────────────────────────
  const handleDelete = async (id: number) => {
    if (!confirm('آیا از حذف این محصول مطمئن هستید؟')) return;
    try {
      const response = await fetch(`https://fellfell.ir/api/products.php?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        showToast('✅ محصول با موفقیت حذف شد', 'success');
        loadProducts();
      } else {
        showToast('❌ خطا در حذف محصول: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('❌ خطا در حذف محصول: ' + err.message, 'error');
    }
  };

  // ── تغییر وضعیت ──────────────────────────────────────
  const handleStatusChange = async (id: number, status: string) => {
    try {
      // ابتدا محصول را دریافت کن تا داده‌های کامل داشته باشیم
      const getResponse = await fetch(`https://fellfell.ir/api/products.php?action=single&id=${id}`, {
        credentials: 'include'
      });
      const getData = await getResponse.json();
      
      if (!getData.success || !getData.data) {
        showToast('❌ خطا در دریافت اطلاعات محصول', 'error');
        return;
      }
      
      const product = getData.data;
      
      // ارسال داده‌های کامل برای بروزرسانی
      const response = await fetch(`https://fellfell.ir/api/products.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: product.name || product.title || '',
          title: product.name || product.title || '',
          price: parseFloat(product.price),
          stock: parseInt(product.stock) || 0,
          description: product.description || '',
          status: status,
          is_featured: product.is_featured || product.is_special || 0,
          image: product.image || null,
          category_id: product.category_id || 1
        })
      });
      
      const data = await response.json();
      if (data.success) {
        showToast(`✅ وضعیت محصول به ${status === 'active' ? 'فعال' : 'غیرفعال'} تغییر یافت`, 'success');
        loadProducts();
      } else {
        showToast('❌ خطا در تغییر وضعیت: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('❌ خطا در تغییر وضعیت: ' + err.message, 'error');
    }
  };

  // ── رفتن به صفحه افزودن ──────────────────────────────
  const goToAddProduct = () => {
    navigate('/admin/products/add');
  };

  // ── رفتن به صفحه ویرایش ──────────────────────────────
  const goToEditProduct = (id: number) => {
    navigate(`/admin/products/edit/${id}`);
  };

  // ── نمایش لودینگ ──────────────────────────────────────
  if (loading) {
    return (
      <div style={{ 
        direction: 'rtl', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '18px',
        color: '#555'
      }}>
        <span>⏳ در حال بارگذاری محصولات...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '15px 20px',
          borderRadius: '10px',
          borderRight: '4px solid #c62828'
        }}>
          ❌ {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {/* ── Toast ────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      {/* ── هدر ──────────────────────────────────────────── */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '25px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h1 style={{ color: '#1e3a1e', fontSize: '24px', margin: 0 }}>
            📦 مدیریت محصولات
          </h1>
          <p style={{ color: '#888', fontSize: '14px', margin: '5px 0 0' }}>
            {products.length} محصول در سیستم وجود دارد
          </p>
        </div>
        <button 
          onClick={goToAddProduct}
          style={{
            padding: '10px 24px',
            background: '#2e7d32',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1b5e20';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#2e7d32';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          ➕ افزودن محصول
        </button>
      </div>

      {/* ── لیست محصولات ────────────────────────────────── */}
      {products.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
          <p style={{ fontSize: '18px', color: '#888' }}>هیچ محصولی یافت نشد</p>
          <p style={{ color: '#aaa', marginTop: '5px' }}>
            برای افزودن محصول روی دکمه <strong>"افزودن محصول"</strong> کلیک کنید
          </p>
          <button
            onClick={goToAddProduct}
            style={{
              marginTop: '20px',
              padding: '10px 30px',
              background: '#2e7d32',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ➕ افزودن اولین محصول
          </button>
        </div>
      ) : (
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px' }}>
              <thead>
                <tr style={{ background: '#f5faf5' }}>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>تصویر</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>نام</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>دسته</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>قیمت</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>موجودی</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>وضعیت</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const productName = p.title || p.name || 'بدون نام';
                  const imagePath = p.image 
                    ? (p.image.startsWith('http') ? p.image : `/uploads/products/${p.image}`)
                    : '/images/default-product.jpg';
                  
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #eef5ee', transition: 'background 0.2s' }}>
                      <td style={{ padding: '12px 15px' }}>
                        <img 
                          src={imagePath}
                          alt={productName}
                          style={{
                            width: '50px',
							    aspectRatio: '1/1',        // ← کلید اصلی

                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #e8f0e8'
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/default-product.jpg';
                          }}
						   loading="lazy"               // ← اضافه شد برای بهینه‌سازی
  decoding="async"             // ← اضافه شد برای بهینه‌سازی
                        />
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        {productName}
                      </td>
                      <td style={{ padding: '12px 15px', color: '#666' }}>
                        {p.category_name || 'نامشخص'}
                      </td>
                      <td style={{ padding: '12px 15px', color: '#2E7D32', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('fa-IR').format(Number(p.price))} تومان
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          background: p.stock > 0 ? '#e8f5e9' : '#ffebee',
                          color: p.stock > 0 ? '#2e7d32' : '#c62828'
                        }}>
                          {p.stock > 0 ? p.stock : 'ناموجود'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                          <span 
                            style={{
                              padding: '3px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              background: p.status === 'active' ? '#e8f5e9' : '#ffebee',
                              color: p.status === 'active' ? '#2e7d32' : '#c62828',
                              cursor: 'pointer',
                              transition: 'opacity 0.2s'
                            }}
                            onClick={() => handleStatusChange(p.id, p.status === 'active' ? 'inactive' : 'active')}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            {p.status === 'active' ? '✅ فعال' : '⛔ غیرفعال'}
                          </span>
                          {(p.is_featured || p.is_special) === 1 && (
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              background: '#fff3e0',
                              color: '#e65100'
                            }}>
                              ⭐ ویژه
                            </span>
                          )}
                        </div>
                      </td>
					  // در قسمت نمایش دسته‌بندی:
<td style={{ padding: '12px 15px' }}>
  {p.category_name === 'بذر و نشا' ? '🌱 بذر و نشا' : 
   p.category_name === 'کود' ? '🧪 کود' : 
   p.category_name === 'سم' ? '💊 سم' : 
   p.category_name || 'نامشخص'}
</td>
                      <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button 
                            onClick={() => goToEditProduct(p.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#e3f2fd',
                              color: '#0d47a1',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#bbdefb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#e3f2fd';
                            }}
                            title="ویرایش محصول"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDelete(p.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ffebee',
                              color: '#c62828',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#ffcdd2';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ffebee';
                            }}
                            title="حذف محصول"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── استایل انیمیشن ────────────────────────────────── */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Products;